import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getAttentionLoops, getAttentionLoop, disposeAttentionLoop,
  promoteLoopToTask, notATask, checkCorrespondence, getAttentionTeam,
  AttentionLoopCard, AttentionLoopDetail, LoopBucket, LoopDispositionAction,
  WorkItemPriority, NotATaskReason, AttentionLoopsResponse, TeamMember,
  setTokenProvider,
} from '../api/client';

// ═══════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════

const NOT_A_TASK_REASONS: Array<{ value: NotATaskReason; label: string }> = [
  { value: 'waiting-on-someone', label: 'Waiting on someone' },
  { value: 'informational', label: 'Informational' },
  { value: 'already-handled', label: 'Already handled' },
  { value: 'delegated', label: 'Delegated' },
  { value: 'no-follow-up', label: 'No follow-up needed' },
  { value: 'routine-notification', label: 'Routine notification' },
  { value: 'other', label: 'Other' },
];

const BUCKETS: Array<{ key: LoopBucket; label: string; hint: string; accent: string }> = [
  { key: 'needs-you', label: 'Needs You', hint: 'Your action or decision is required', accent: 'border-l-red-500' },
  { key: 'waiting', label: 'Waiting On', hint: "You're waiting on someone else", accent: 'border-l-amber-500' },
  { key: 'delegated', label: 'Delegated', hint: 'Assigned to someone on your team', accent: 'border-l-purple-500' },
  { key: 'conditional', label: 'Conditional', hint: 'Blocked on a dependency or future condition', accent: 'border-l-blue-500' },
  { key: 'awareness', label: 'Awareness', hint: 'For your awareness — no action owned', accent: 'border-l-gray-300' },
];

type SortMode = 'recommended' | 'risk' | 'latest' | 'oldest' | 'project';
type Density = 'comfortable' | 'compact';
const DENSITY_KEY = 'attention-density';
const SORT_KEY = 'attention-sort';

const RISK_CHIP: Record<string, string> = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-gray-100 text-gray-600', none: 'bg-gray-50 text-gray-400' };

function relativeTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const m = Math.floor(diffMs / 60000); const h = Math.floor(m / 60); const d = Math.floor(h / 24);
  const abs = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ', ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const rel = m < 1 ? 'just now' : m < 60 ? `${m}m ago` : h < 24 ? `${h}h ago` : d < 7 ? `${d}d ago` : '';
  return rel ? `${abs} · ${rel}` : abs;
}

function sortItems(items: AttentionLoopCard[], mode: SortMode): AttentionLoopCard[] {
  const s = [...items];
  switch (mode) {
    case 'risk': return s.sort((a, b) => ({ high: 3, medium: 2, low: 1, none: 0 }[b.operationalRisk] || 0) - ({ high: 3, medium: 2, low: 1, none: 0 }[a.operationalRisk] || 0));
    case 'latest': return s.sort((a, b) => (b.latestActivityAt || '').localeCompare(a.latestActivityAt || ''));
    case 'oldest': return s.sort((a, b) => (a.latestActivityAt || '').localeCompare(b.latestActivityAt || ''));
    case 'project': return s.sort((a, b) => (a.project?.projectDisplayName || '~').localeCompare(b.project?.projectDisplayName || '~'));
    default: return s; // 'recommended' uses server sort (urgency + project cluster)
  }
}

// ═══════════════════════════════════════════════════════════
// PAGE
// ═══════════════════════════════════════════════════════════

export function AttentionQueuePage() {
  const { getToken } = useAuth();
  const [data, setData] = useState<AttentionLoopsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeBucket, setActiveBucket] = useState<LoopBucket | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail] = useState<AttentionLoopDetail | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [createFor, setCreateFor] = useState<AttentionLoopCard | null>(null);
  const [density, setDensity] = useState<Density>(() => (localStorage.getItem(DENSITY_KEY) as Density) || 'comfortable');
  const [sort, setSort] = useState<SortMode>(() => (localStorage.getItem(SORT_KEY) as SortMode) || 'recommended');
  const [search, setSearch] = useState('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [successMsg, setSuccessMsg] = useState('');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => { setTokenProvider(getToken); load(); loadTeam(); }, []);
  useEffect(() => { localStorage.setItem(DENSITY_KEY, density); }, [density]);
  useEffect(() => { localStorage.setItem(SORT_KEY, sort); }, [sort]);

  async function load() { setLoading(true); setError(''); try { setData(await getAttentionLoops(true)); } catch (e: any) { setError(e.message || 'Failed to load'); } finally { setLoading(false); } }
  async function loadTeam() { try { const res = await getAttentionTeam(); setTeamMembers(res.members || []); } catch {} }

  function groups() { return data?.groups || { 'needs-you': [], waiting: [], delegated: [], conditional: [], awareness: [] }; }
  function counts() { return data?.counts || { 'needs-you': 0, waiting: 0, delegated: 0, conditional: 0, awareness: 0, archived: 0, suppressedRoutine: 0, clearedForMe: 0 }; }

  function filterBySearch(items: AttentionLoopCard[]): AttentionLoopCard[] {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((l) =>
      (l.subject || '').toLowerCase().includes(q) ||
      (l.summary || '').toLowerCase().includes(q) ||
      (l.project?.projectDisplayName || '').toLowerCase().includes(q) ||
      (l.currentActor?.displayName || '').toLowerCase().includes(q) ||
      (l.waitingOnParty?.displayName || '').toLowerCase().includes(q) ||
      (l.delegatedTo?.displayName || '').toLowerCase().includes(q)
    );
  }

  function patchCard(loopId: string, patch: Partial<AttentionLoopCard>) {
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, groups: { ...prev.groups } };
      (Object.keys(next.groups) as LoopBucket[]).forEach((k) => { next.groups[k] = next.groups[k].map((l) => l.loopId === loopId ? { ...l, ...patch } : l); });
      return next;
    });
  }
  function removeCard(loopId: string) {
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, groups: { ...prev.groups } };
      (Object.keys(next.groups) as LoopBucket[]).forEach((k) => { next.groups[k] = next.groups[k].filter((l) => l.loopId !== loopId); });
      return next;
    });
  }

  async function dispose(loopId: string, action: LoopDispositionAction) { setBusyId(loopId); try { await disposeAttentionLoop(loopId, action); removeCard(loopId); if (expanded === loopId) { setExpanded(null); setDetail(null); } } catch (e: any) { setError(e.message); } finally { setBusyId(null); } }
  async function delegate(loopId: string, userId: string) {
    const member = teamMembers.find((m) => m.userId === userId);
    setBusyId(loopId);
    try {
      const res = await disposeAttentionLoop(loopId, 'set-delegated', undefined, { delegatedToUserId: userId });
      // Move the card from its current bucket to the delegated group immediately.
      setData((prev) => {
        if (!prev) return prev;
        const next = { ...prev, groups: { ...prev.groups } };
        // Remove from all groups
        (Object.keys(next.groups) as LoopBucket[]).forEach((k) => { next.groups[k] = next.groups[k].filter((l) => l.loopId !== loopId); });
        // Insert the updated card into delegated
        if (res.loop) { next.groups.delegated = [res.loop, ...next.groups.delegated]; }
        return next;
      });
      if (expanded === loopId) { setExpanded(null); setDetail(null); }
      setSuccessMsg(`Delegated to ${member?.name || res.loop?.delegatedTo?.displayName || 'team member'}`);
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (e: any) { setError(e.message); }
    finally { setBusyId(null); }
  }
  async function toggleDetail(loopId: string) { if (expanded === loopId) { setExpanded(null); setDetail(null); return; } setExpanded(loopId); setDetail(null); try { setDetail(await getAttentionLoop(loopId)); } catch {} }
  async function submitCreate(input: { title: string; dueDate?: string; priority: WorkItemPriority; projectKey?: string; projectDisplayName?: string; notes?: string }) { if (!createFor) return; const loopId = createFor.loopId; setBusyId(loopId); try { const res = await promoteLoopToTask(loopId, input); removeCard(loopId); setCreateFor(null); } catch (e: any) { setError(e.message); } finally { setBusyId(null); } }
  async function submitNotATask(loopId: string, reason: NotATaskReason) { setBusyId(loopId); try { await notATask(loopId, reason); patchCard(loopId, { taskSuggestionDismissed: true }); } catch (e: any) { setError(e.message); } finally { setBusyId(null); } }

  async function doCheckCorrespondence(loopId: string) {
    setBusyId(loopId);
    try {
      const r = await checkCorrespondence(loopId);
      if (r.found > 0) {
        setDetail(await getAttentionLoop(loopId));
        patchCard(loopId, { evidenceCount: (groups()['needs-you']?.find((l) => l.loopId === loopId)?.evidenceCount || 0) + r.found });
      }
      setError(r.message);
      setTimeout(() => setError(''), 5000);
    } catch (e: any) { setError(e.message); }
    finally { setBusyId(null); }
  }

  function scrollTo(bucket: LoopBucket) { setActiveBucket(bucket); sectionRefs.current[bucket]?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }

  const visibleBuckets = BUCKETS.filter((b) => (groups()[b.key]?.length || 0) > 0);
  const totalActive = BUCKETS.reduce((n, b) => n + (groups()[b.key]?.length || 0), 0);

  return (
    <div className="h-full flex">
      {/* Sidebar navigation (desktop) */}
      <aside className="hidden lg:flex flex-col w-52 border-r border-gray-200 bg-gray-50 py-4 px-3 flex-shrink-0 overflow-y-auto">
        <h2 className="text-[11px] font-semibold text-gray-500 uppercase mb-3 px-2">Categories</h2>
        {BUCKETS.map((b) => {
          const c = groups()[b.key]?.length || 0;
          const active = activeBucket === b.key;
          return (
            <button key={b.key} onClick={() => scrollTo(b.key)} className={`flex items-center justify-between px-2 py-1.5 rounded text-sm ${active ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-600 hover:bg-white/60'}`}>
              <span>{b.label}</span>
              {c > 0 && <span className="text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full">{c}</span>}
            </button>
          );
        })}
        <div className="mt-auto pt-4 border-t border-gray-200 text-xs text-gray-400 px-2 space-y-1">
          {(counts().suppressedRoutine || 0) > 0 && <div>{counts().suppressedRoutine} routine hidden</div>}
          {(counts().clearedForMe || 0) > 0 && <div>{counts().clearedForMe} done for you</div>}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Attention Queue <span className="text-xs text-gray-400 font-normal">(DEV)</span></h1>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>{totalActive} active</span>
                {data?.lastSyncAt && <span title="Last time email was synced from Graph">Email sync: {new Date(data.lastSyncAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search attention…" className="text-xs border border-gray-300 rounded px-2 py-1 w-40" />
              <select value={sort} onChange={(e) => setSort(e.target.value as SortMode)} className="text-xs border border-gray-300 rounded px-2 py-1">
                <option value="recommended">Recommended</option>
                <option value="risk">Risk</option>
                <option value="latest">Latest Activity</option>
                <option value="oldest">Oldest Activity</option>
                <option value="project">Project</option>
              </select>
              <button onClick={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')} className="text-xs border border-gray-300 rounded px-2 py-1" title="Toggle density">
                {density === 'compact' ? '▤ Comfortable' : '≡ Compact'}
              </button>
              <button onClick={load} className="text-xs text-[#4AABDE] hover:underline">&#8635;</button>
            </div>
          </div>

          {error && <div className="bg-red-50 text-red-700 px-3 py-2 rounded mb-3 text-sm">{error} <button onClick={() => setError('')} className="float-right font-bold">&times;</button></div>}
          {successMsg && <div className="bg-green-50 text-green-700 px-3 py-2 rounded mb-3 text-sm">{successMsg}</div>}

          {loading ? <div className="py-12 text-center text-gray-500">Loading…</div> : totalActive === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center"><p className="text-gray-500 text-sm">No active open loops.</p></div>
          ) : (
            <div className="space-y-6">
              {visibleBuckets.map((bucket) => {
                const items = sortItems(filterBySearch(groups()[bucket.key] || []), sort);
                return (
                  <section key={bucket.key} ref={(el) => { sectionRefs.current[bucket.key] = el; }}>
                    <div className="flex items-baseline gap-2 mb-2">
                      <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">{bucket.label}</h2>
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{items.length}</span>
                      <span className="text-xs text-gray-400 hidden sm:inline">{bucket.hint}</span>
                    </div>
                    <div className={density === 'compact' ? 'space-y-1' : 'space-y-3'}>
                      {items.map((loop) => density === 'compact'
                        ? <CompactCard key={loop.loopId} loop={loop} accent={bucket.accent} busy={busyId === loop.loopId}
                            onToggle={() => toggleDetail(loop.loopId)} onDispose={(a) => dispose(loop.loopId, a)}
                            onDelegate={(userId) => delegate(loop.loopId, userId)} teamMembers={teamMembers}
                            onCreateTask={() => setCreateFor(loop)} onNotATask={(r) => submitNotATask(loop.loopId, r)}
                            onCheckCorrespondence={() => doCheckCorrespondence(loop.loopId)} />
                        : <ComfortableCard key={loop.loopId} loop={loop} accent={bucket.accent} expanded={expanded === loop.loopId}
                            detail={expanded === loop.loopId ? detail : null} busy={busyId === loop.loopId}
                            onToggle={() => toggleDetail(loop.loopId)} onDispose={(a) => dispose(loop.loopId, a)}
                            onDelegate={(userId) => delegate(loop.loopId, userId)} teamMembers={teamMembers}
                            onCreateTask={() => setCreateFor(loop)} onNotATask={(r) => submitNotATask(loop.loopId, r)}
                            onCheckCorrespondence={() => doCheckCorrespondence(loop.loopId)} />
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create Task Modal — fixed portal, background scroll cannot dismiss */}
      {createFor && <CreateTaskModal loop={createFor} busy={busyId === createFor.loopId} onCancel={() => setCreateFor(null)} onSubmit={submitCreate} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// COMPACT CARD (dense one-line)
// ═══════════════════════════════════════════════════════════

function CompactCard({ loop, accent, busy, onToggle, onDispose, onDelegate, teamMembers, onCreateTask, onNotATask, onCheckCorrespondence }: CardProps) {
  const promoted = !!loop.promotedWorkItemId;
  return (
    <div className={`flex items-center gap-2 bg-white border border-gray-200 border-l-4 ${accent} rounded px-3 py-1.5 text-xs hover:shadow-sm`}>
      {loop.project?.projectDisplayName && <span className="text-[10px] px-1 py-0.5 rounded bg-[#4AABDE]/10 text-[#2a7fb8] truncate max-w-[80px]">{loop.project.projectDisplayName}</span>}
      <span className="font-medium text-gray-900 truncate flex-1">{loop.subject || '(no subject)'}</span>
      {loop.operationalRisk !== 'none' && <span className={`text-[10px] px-1 py-0.5 rounded-full ${RISK_CHIP[loop.operationalRisk]}`}>{loop.operationalRisk}</span>}
      <span className="text-gray-400 whitespace-nowrap">{relativeTime(loop.latestActivityAt)}</span>
      {promoted ? <span className="text-green-700 text-[10px]">&#10003; Task</span> : (
        <button disabled={busy} onClick={onCreateTask} className="text-[10px] text-[#2a7fb8] hover:underline disabled:opacity-40 whitespace-nowrap">+ Task</button>
      )}
      <button disabled={busy} onClick={() => onDispose('done-for-me')} className="text-[10px] text-gray-500 hover:text-[#2a7fb8] disabled:opacity-40">Done</button>
      <button disabled={busy} onClick={() => onDispose('resolve')} className="text-green-600 hover:bg-green-50 rounded p-0.5 disabled:opacity-40">&#10003;</button>
      <button disabled={busy} onClick={() => onDispose('dismiss')} className="text-gray-400 hover:bg-gray-100 rounded p-0.5 disabled:opacity-40">&times;</button>
      <button onClick={onToggle} className="text-[#4AABDE] hover:underline">{loop.evidenceCount}</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// COMFORTABLE CARD (rich context)
// ═══════════════════════════════════════════════════════════

interface CardProps {
  loop: AttentionLoopCard; accent: string; busy: boolean;
  onToggle: () => void; onDispose: (a: LoopDispositionAction) => void;
  onDelegate: (userId: string) => void;
  teamMembers: TeamMember[];
  onCreateTask: () => void; onNotATask: (r: NotATaskReason) => void;
  onCheckCorrespondence: () => void;
  expanded?: boolean; detail?: AttentionLoopDetail | null;
}

function ComfortableCard({ loop, accent, expanded, detail, busy, onToggle, onDispose, onDelegate, teamMembers, onCreateTask, onNotATask, onCheckCorrespondence }: CardProps) {
  const [showReasons, setShowReasons] = useState(false);
  const [showDelegate, setShowDelegate] = useState(false);
  const [selectedDelegate, setSelectedDelegate] = useState('');
  const promoted = !!loop.promotedWorkItemId;
  const suggestible = !promoted && !loop.taskSuggestionDismissed && loop.bucket !== 'awareness';
  const who = loop.waitingOnParty?.displayName ? `Waiting on ${loop.waitingOnParty.displayName}` : loop.delegatedTo?.displayName ? `Delegated to ${loop.delegatedTo.displayName}` : loop.currentActor?.displayName ? `Next: ${loop.currentActor.displayName}` : null;

  return (
    <div className={`bg-white rounded-lg border border-gray-200 border-l-4 ${accent} p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {loop.project?.projectDisplayName && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#4AABDE]/10 text-[#2a7fb8] font-medium">{loop.project.projectDisplayName}</span>}
            <h3 className="font-medium text-gray-900 text-sm truncate">{loop.subject || '(no subject)'}</h3>
            {loop.operationalRisk !== 'none' && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${RISK_CHIP[loop.operationalRisk]}`}>{loop.operationalRisk} risk</span>}
            {loop.personalActionComplete && loop.bucket !== 'needs-you' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700">your part done</span>}
          </div>
          {loop.summary && <p className="text-gray-600 text-xs mt-1 line-clamp-2">{loop.summary}</p>}
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
            {who && <span className="text-gray-600">{who}</span>}
            {loop.dependency && <span>&#9195; {loop.dependency}</span>}
            <span className="text-gray-400">&#128337; {relativeTime(loop.latestActivityAt)}</span>
            {loop.evidenceSource && <span className="text-gray-400">{loop.evidenceSource === 'both' ? 'Inbox+Sent' : loop.evidenceSource === 'sent' ? 'Sent' : 'Inbox'}</span>}
            <button onClick={onToggle} className="text-[#4AABDE] hover:underline">{expanded ? 'Hide' : `Why? (${loop.evidenceCount})`}</button>
          </div>

          {/* Promotion row */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {promoted ? (
              <><span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">&#10003; In My Work</span>
              <a href={`/my-work?open=${loop.promotedWorkItemId}`} className="text-[11px] text-[#2a7fb8] hover:underline">Open task &rarr;</a></>
            ) : suggestible ? (
              <><button disabled={busy} onClick={onCreateTask} className="text-[11px] px-2 py-1 rounded bg-[#1a2332] text-white hover:bg-[#243044] disabled:opacity-40">+ Create Task</button>
              <button disabled={busy} onClick={() => setShowReasons((v) => !v)} className="text-[11px] text-gray-400 hover:text-gray-600">Not a task</button>
              {showReasons && <span className="flex flex-wrap gap-1">{NOT_A_TASK_REASONS.map((r) => <button key={r.value} disabled={busy} onClick={() => { setShowReasons(false); onNotATask(r.value); }} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40">{r.label}</button>)}</span>}</>
            ) : loop.taskSuggestionDismissed ? <span className="text-[11px] text-gray-400">Marked not a task</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button disabled={busy} onClick={() => onDispose('done-for-me')} title="Done for me" className="px-2 py-1 text-[11px] text-[#2a7fb8] hover:bg-[#4AABDE]/10 rounded disabled:opacity-40 whitespace-nowrap">Done for me</button>
          {loop.bucket === 'needs-you' && !showDelegate && <button disabled={busy} onClick={() => setShowDelegate(true)} title="Delegate to someone" className="px-2 py-1 text-[11px] text-purple-600 hover:bg-purple-50 rounded disabled:opacity-40 whitespace-nowrap">Delegate</button>}
          <button disabled={busy} onClick={() => onDispose('resolve')} title="Resolve" className="p-1.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-40">&#10003;</button>
          <button disabled={busy} onClick={() => onDispose('dismiss')} title="Dismiss" className="p-1.5 text-gray-400 hover:bg-gray-100 rounded disabled:opacity-40">&times;</button>
        </div>
      </div>

      {/* Delegate inline form */}
      {showDelegate && (
        <div className="mt-2 flex items-center gap-2 pl-1">
          <select
            autoFocus value={selectedDelegate}
            onChange={(e) => setSelectedDelegate(e.target.value)}
            className="text-[12px] px-2 py-1 border border-gray-300 rounded w-48 focus:outline-none focus:border-purple-400"
          >
            <option value="">Select team member…</option>
            {teamMembers.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
          </select>
          <button disabled={busy || !selectedDelegate} onClick={() => { onDelegate(selectedDelegate); setShowDelegate(false); setSelectedDelegate(''); }} className="text-[11px] px-2 py-1 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40">Confirm</button>
          <button onClick={() => { setShowDelegate(false); setSelectedDelegate(''); }} className="text-[11px] text-gray-400 hover:text-gray-600">Cancel</button>
        </div>
      )}

      {/* Expanded detail */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600 space-y-2">
          {!detail ? <div className="text-gray-400">Loading provenance…</div> : (
            <>
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">Evidence ({detail.evidence.length}):</span>
                <button disabled={busy} onClick={onCheckCorrespondence} className="text-[11px] text-[#2a7fb8] hover:underline disabled:opacity-40">Check for more correspondence</button>
              </div>
              <EvidenceList evidence={detail.evidence} />
              {detail.commitments.length > 0 && <div><span className="font-medium text-gray-700">Commitments:</span> {detail.commitments.map((c) => `${c.who || '?'}: ${c.description}`).join('; ')}</div>}
              {detail.linkedWorkItem && <div className="text-gray-600"><span className="font-medium text-gray-700">Linked task:</span> {detail.linkedWorkItem.title} — <span className="capitalize">{detail.linkedWorkItem.status}</span></div>}
              {detail.completionRecommendation?.workItemCompleted && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  <div className="text-amber-800 font-medium">Task complete — confirm what happens next</div>
                  <p className="text-amber-700 text-[11px] mt-1">{detail.completionRecommendation.note}</p>
                  <div className="flex flex-wrap gap-2 mt-2">{detail.completionRecommendation.suggestions.filter((s) => s.applies && s.action !== 'none').map((s, i) => <button key={i} disabled={busy} onClick={() => onDispose(s.action as LoopDispositionAction)} className="text-[11px] px-2 py-1 rounded bg-amber-600 text-white disabled:opacity-40">{s.label}</button>)}</div>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <button disabled={busy} onClick={() => onDispose('resolve')} className="px-2 py-1 bg-green-600 text-white rounded disabled:opacity-40">Resolve</button>
                <button disabled={busy} onClick={() => onDispose('done-for-me')} className="px-2 py-1 bg-[#4AABDE] text-white rounded disabled:opacity-40">Done for me</button>
                <button disabled={busy} onClick={() => onDispose('close')} className="px-2 py-1 bg-gray-600 text-white rounded disabled:opacity-40">Close</button>
                <button disabled={busy} onClick={() => onDispose('dismiss')} className="px-2 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-40">Dismiss</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════
// EVIDENCE LIST (Corrections 2+3: full inspection + direction/identity)
// ═══════════════════════════════════════════════════════════

function EvidenceList({ evidence }: { evidence: Array<{ source: string; folder: string; timestamp: string; contribution: string; summary: string; conversationId: string; direction?: string; from?: string; to?: string; subject?: string }> }) {
  const INITIAL_SHOW = 6;
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? evidence : evidence.slice(0, INITIAL_SHOW);
  const hasMore = evidence.length > INITIAL_SHOW;

  return (
    <div className="mt-1">
      <ul className="space-y-1.5">
        {visible.map((e, i) => (
          <li key={i} className="text-xs border-l-2 pl-2 py-0.5" style={{ borderColor: e.direction === 'outbound' ? '#4AABDE' : '#d1d5db' }}>
            <div className="flex items-center gap-2 text-gray-600">
              <span className={`text-[10px] px-1 py-0.5 rounded ${e.direction === 'outbound' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                {e.direction === 'outbound' ? '→ Sent' : '← Received'}
              </span>
              <span className="text-gray-400">{new Date(e.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, {new Date(e.timestamp).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>
              <span className="text-[10px] text-gray-400">{e.contribution}</span>
            </div>
            <div className="text-gray-700 mt-0.5">
              {e.from && <span className="font-medium">{e.from}</span>}
              {e.from && e.to && <span className="text-gray-400"> → </span>}
              {e.to && <span>{e.to}</span>}
            </div>
            {e.summary && <div className="text-gray-500 truncate">{e.summary}</div>}
          </li>
        ))}
      </ul>
      {hasMore && !showAll && (
        <button onClick={() => setShowAll(true)} className="mt-1.5 text-[11px] text-[#4AABDE] hover:underline">
          Show all {evidence.length} evidence records
        </button>
      )}
      {hasMore && showAll && (
        <button onClick={() => setShowAll(false)} className="mt-1.5 text-[11px] text-gray-400 hover:underline">
          Show fewer
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CREATE TASK MODAL — uses fixed positioning so background scroll cannot dismiss
// ═══════════════════════════════════════════════════════════

function CreateTaskModal({ loop, busy, onCancel, onSubmit }: { loop: AttentionLoopCard; busy: boolean; onCancel: () => void; onSubmit: (input: { title: string; dueDate?: string; priority: WorkItemPriority; projectKey?: string; projectDisplayName?: string; notes?: string }) => void }) {
  const [title, setTitle] = useState(loop.suggestedTitle || loop.summary || loop.subject || '');
  const [priority, setPriority] = useState<WorkItemPriority>(loop.suggestedPriority || 'medium');
  const [dueDate, setDueDate] = useState('');
  const [projectDisplayName, setProjectDisplayName] = useState(loop.project?.projectDisplayName || '');
  const [notes, setNotes] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);

  // Prevent background scroll while modal is open.
  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }, []);

  return (
    <div ref={overlayRef} className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onMouseDown={(e) => { if (e.target === overlayRef.current) onCancel(); }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5" onMouseDown={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900">Create Task</h3>
        <p className="text-xs text-gray-500 mb-3">Review and edit — a My Work task is created when you click Create.</p>
        <label className="block text-xs font-medium text-gray-700 mt-2">Task <span className="text-gray-400">(action-oriented)</span></label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div><label className="block text-xs font-medium text-gray-700">Priority</label><select value={priority} onChange={(e) => setPriority(e.target.value as WorkItemPriority)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option></select></div>
          <div><label className="block text-xs font-medium text-gray-700">Due date <span className="text-gray-400">(optional)</span></label><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
        </div>
        <label className="block text-xs font-medium text-gray-700 mt-3">Job / Project <span className="text-gray-400">(optional)</span></label>
        <input value={projectDisplayName} onChange={(e) => setProjectDisplayName(e.target.value)} placeholder="Associate with a job…" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
        <label className="block text-xs font-medium text-gray-700 mt-3">Notes <span className="text-gray-400">(optional)</span></label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
        <p className="text-[11px] text-gray-400 mt-3">Assigned to you. Reassign in My Work. Creating this task will not change the Open Loop's state.</p>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
          <button disabled={busy || !title.trim()} onClick={() => onSubmit({ title: title.trim(), dueDate: dueDate || undefined, priority, projectKey: loop.project?.projectKey, projectDisplayName: projectDisplayName.trim() || undefined, notes: notes.trim() || undefined })} className="px-3 py-1.5 text-sm bg-[#4AABDE] text-white rounded disabled:opacity-40">{busy ? 'Creating…' : 'Create Task'}</button>
        </div>
      </div>
    </div>
  );
}
